import {useRef} from "react";

const diameter = 50;
const strokeWidth = diameter / 2;
const radius = strokeWidth / 2;
const circumference = Math.PI * radius * 2;
const polylineLength = 32.5;

type CircularProgressProps = {
    value: number;
    max: number;
    className?: string;
};

export const CircularProgress = ({
    value,
    max,
    className,
}: CircularProgressProps) => {
    const completion = max === 0 ? 0 : (value / max) * 100;
    const completedOnMount = useRef(completion === 100);

    const dashOffset = ((100 - completion) / 100) * circumference;
    const strokeColor = completion === 100 ? "#428C37" : "#F28650";
    const trackStroke =
        value > 0 ? "var(--mantine-color-neutral-3)" : "transparent";
    const skipAnimation = completedOnMount.current;

    return (
        <div
            className={className}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={max}
            aria-valuenow={value}
        >
            <svg
                viewBox={`0 0 ${diameter} ${diameter}`}
                xmlns="http://www.w3.org/2000/svg"
                style={{width: "100%", height: "100%"}}
            >
                {/* Track circle */}
                <circle
                    cx={strokeWidth}
                    cy={strokeWidth}
                    r={radius}
                    fill="transparent"
                    strokeWidth={strokeWidth}
                    stroke={trackStroke}
                    style={{
                        transition: skipAnimation
                            ? "none"
                            : "stroke 0.5s linear",
                    }}
                />
                {/* Indicator circle */}
                <circle
                    cx={strokeWidth}
                    cy={strokeWidth}
                    r={radius}
                    fill="transparent"
                    strokeWidth={strokeWidth}
                    stroke={strokeColor}
                    strokeDasharray={circumference}
                    strokeDashoffset={dashOffset}
                    style={{
                        transformOrigin: "50% 50%",
                        transform: "rotate(-90deg)",
                        transition: skipAnimation
                            ? "none"
                            : "stroke-dashoffset 0.5s ease, stroke 0.5s linear",
                    }}
                />
                {/* Checkmark polyline */}
                {completion === 100 && (
                    <polyline
                        points="14.1,25.3 20.7,31.8 36.4,16"
                        stroke="#fff"
                        fill="none"
                        strokeWidth="4"
                        strokeDasharray={`${polylineLength}, ${polylineLength}`}
                        strokeDashoffset={skipAnimation ? 0 : undefined}
                        style={{
                            strokeDashoffset: skipAnimation ? 0 : 0,
                            transition: skipAnimation
                                ? "none"
                                : "stroke-dashoffset 0.3s ease 0.5s",
                        }}
                    />
                )}
            </svg>
        </div>
    );
};
